/**
 * App Root — Routing for both Org Users and Ops Users
 *
 * Auth guards:
 *   - ProtectedRoute: redirects unauthenticated users to login
 *   - PublicRoute: redirects authenticated users to their home
 *   - OpsOnly: only ops users can access these routes
 *   - OrgOrOpsWithAccess: org users or ops users with active access token
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// AntD derives its whole palette from these values, so they must be real colour
// strings — the one place `var(--brand)` cannot be used. See lib/brandTokens.ts.
import {
  BRAND, BRAND_600, BRAND_700, BRAND_400, BRAND_300,
  BRAND_100, BRAND_50, BRAND_BORDER,
} from './lib/brandTokens';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, Spin } from 'antd';
import { LocaleProvider, useLocale } from './i18n/LocaleProvider';
import { AuthProvider, useAuth } from './context/AuthContext';
import { canSeeAccountingBillsPage } from './lib/accountingSurface';
import AppLayout from './components/AppLayout';
// ─── Route-level code splitting ──────────────────────────────────────────────
// Every page is its own chunk, fetched when its route is first visited. These
// used to be static imports, which folded all ~100 pages (plus recharts,
// react-big-calendar, dnd-kit, …) into ONE 1.6 MB entry bundle that every
// visitor — even a vendor opening a magic link — downloaded and parsed before
// anything rendered (Lighthouse: FCP 10 s, 70% of the JS unused on load).
// Layout/guard components stay static: they are the shell, needed on every route.
// Suspense boundaries: AppLayout + VendorPortalLayout wrap their <Outlet/> (the
// shell stays on screen while a page chunk loads); the <Routes> below covers
// the standalone public pages.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const OpsLoginPage = lazy(() => import('./pages/OpsLoginPage'));
const loadDashboardPage = () => import('./pages/DashboardPage');
const DashboardPage = lazy(loadDashboardPage);
const loadWorkOrdersPage = () => import('./pages/WorkOrdersPage');
const WorkOrdersPage = lazy(loadWorkOrdersPage);
const WorkOrderDetailPage = lazy(() => import('./pages/WorkOrderDetailPage'));
const CreateWorkOrderPage = lazy(() => import('./pages/CreateWorkOrderPage'));
const EditWorkOrderPage = lazy(() => import('./pages/EditWorkOrderPage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const VendorDetailPage = lazy(() => import('./pages/VendorDetailPage'));
const VendorCreatePage = lazy(() => import('./pages/VendorCreatePage'));
const VendorEditPage = lazy(() => import('./pages/VendorEditPage'));
const PropertiesPage = lazy(() => import('./pages/PropertiesPage'));
const PropertyCreatePage = lazy(() => import('./pages/PropertyCreatePage'));
const PropertyEditPage = lazy(() => import('./pages/PropertyEditPage'));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage'));
const UnitDetailPage = lazy(() => import('./pages/UnitDetailPage'));
const ManualPropertiesCsvUploadPage = lazy(() => import('./pages/ManualPropertiesCsvUploadPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const TeamInviteCsvPage = lazy(() => import('./pages/TeamInviteCsvPage'));
const AccountSettingsPage = lazy(() => import('./pages/AccountSettingsPage'));
const DispatchRulesPage = lazy(() => import('./pages/DispatchRulesPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const VendorPreferencesPage = lazy(() => import('./pages/VendorPreferencesPage'));
const RepairReplaceRulesPage = lazy(() => import('./pages/RepairReplaceRulesPage'));
const NTEConfigPage = lazy(() => import('./pages/NTEConfigPage'));
const AssetRegistryPage = lazy(() => import('./pages/AssetRegistryPage'));
const AssetCreatePage = lazy(() => import('./pages/AssetCreatePage'));
const AssetDetailPage = lazy(() => import('./pages/AssetDetailPage'));
const AssetEditPage = lazy(() => import('./pages/AssetEditPage'));
const WorkOrderTemplatesPage = lazy(() => import('./pages/WorkOrderTemplatesPage'));
const ScheduledActionsPage = lazy(() => import('./pages/ScheduledActionsPage'));
const AIMemoryPage = lazy(() => import('./pages/AIMemoryPage'));
const CoordinatorCoachPage = lazy(() => import('./pages/CoordinatorCoachPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const AppFolioOnboardingPage = lazy(() => import('./pages/AppFolioOnboardingPage'));
const ConnectedAccountsPage = lazy(() => import('./pages/ConnectedAccountsPage'));
const YardiOnboardingPage = lazy(() => import('./pages/YardiOnboardingPage'));
const BuildiumOnboardingPage = lazy(() => import('./pages/BuildiumOnboardingPage'));
const SLAConfigPage = lazy(() => import('./pages/SLAConfigPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const AccountingBillsPage = lazy(() => import('./pages/AccountingBillsPage'));
const AccountingTurnTaskPage = lazy(() => import('./pages/AccountingTurnTaskPage'));
const AccountingWorkOrderTaskPage = lazy(() => import('./pages/AccountingWorkOrderTaskPage'));
const LeasingTurnTaskPage = lazy(() => import('./pages/LeasingTurnTaskPage'));
const SurveysPage = lazy(() => import('./pages/SurveysPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const ComparisonPage = lazy(() => import('./pages/dashboard/ComparisonPage'));
const AiValuePage = lazy(() => import('./pages/dashboard/AiValuePage'));
const ItemsChangedPage = lazy(() => import('./pages/dashboard/ItemsChangedPage'));
const AllMetricsPage = lazy(() => import('./pages/dashboard/AllMetricsPage'));
const MakeReadyAnalyticsPage = lazy(() => import('./pages/dashboard/MakeReadyAnalyticsPage'));
import { analyticsV2Redirect } from './pages/dashboard/subPageScope';
const OwnersPage = lazy(() => import('./pages/OwnersPage'));
const OwnerDashboardPage = lazy(() => import('./pages/OwnerDashboardPage'));
const InspectionsPage = lazy(() => import('./pages/InspectionsPage'));
const InspectionTemplatesPage = lazy(() => import('./pages/InspectionTemplatesPage'));
const InspectionTemplateDetailPage = lazy(() => import('./pages/InspectionTemplateDetailPage'));
const InspectionTemplateHistoryPage = lazy(() => import('./pages/InspectionTemplateHistoryPage'));
const InspectionCategoriesPage = lazy(() => import('./pages/InspectionCategoriesPage'));
const InspectionWalkPage = lazy(() => import('./pages/InspectionWalkPage'));
const InspectionCampaignPage = lazy(() => import('./pages/InspectionCampaignPage'));
const CreateWorkOrderFromFindingsPage = lazy(() => import('./pages/CreateWorkOrderFromFindingsPage'));
const ActionItemsPage = lazy(() => import('./pages/ActionItemsPage'));
const ComplianceRegistryPage = lazy(() => import('./pages/ComplianceRegistryPage'));
const ComplianceObligationDetailPage = lazy(() => import('./pages/ComplianceObligationDetailPage'));
const NoticesPage = lazy(() => import('./pages/NoticesPage'));
const DispositionsPage = lazy(() => import('./pages/DispositionsPage'));
const DispositionPriceListPage = lazy(() => import('./pages/DispositionPriceListPage'));
const DispositionDetailPage = lazy(() => import('./pages/DispositionDetailPage'));
const RecurringInspectionsPage = lazy(() => import('./pages/RecurringInspectionsPage'));
const RecurringInspectionDetailPage = lazy(() => import('./pages/RecurringInspectionDetailPage'));
const VendorBatchesPage = lazy(() => import('./pages/VendorBatchesPage'));
const EstimatesPage = lazy(() => import('./pages/EstimatesPage'));
const loadMyQueuePage = () => import('./pages/MyQueuePage');
const MyQueuePage = lazy(loadMyQueuePage);
// The mailbox Inbox (spec 170) — one page per person, their own mailbox only.
const InboxPage = lazy(() => import('./pages/InboxPage'));
// Make-Ready module (spec 99): list + unit-folder pages.
const loadMakeReadyListPage = () => import('./pages/MakeReadyListPage');
const MakeReadyListPage = lazy(loadMakeReadyListPage);
const MakeReadyFolderPage = lazy(() => import('./pages/MakeReadyFolderPage'));
const MakeReadySettingsPage = lazy(() => import('./pages/MakeReadySettingsPage'));
const VendorPortalPreviewPage = lazy(() => import('./pages/VendorPortalPreviewPage'));
const OrgListPage = lazy(() => import('./pages/admin/OrgListPage'));
const OrgDetailPage = lazy(() => import('./pages/admin/OrgDetailPage'));
const CreateOrgPage = lazy(() => import('./pages/admin/CreateOrgPage'));
const AccessTokensPage = lazy(() => import('./pages/admin/AccessTokensPage'));
const OpsTeamPage = lazy(() => import('./pages/admin/OpsTeamPage'));
const DemoAccountNewPage = lazy(() => import('./pages/admin/DemoAccountNewPage'));
const ResidentTestPage = lazy(() => import('./pages/ResidentTestPage'));
const ResidentUploadPage = lazy(() => import('./pages/ResidentUploadPage'));
const VendorJobPage = lazy(() => import('./pages/VendorJobPage'));
const CascadeActionPage = lazy(() => import('./pages/CascadeActionPage'));
const PublicTurnSummaryPage = lazy(() => import('./pages/PublicTurnSummaryPage'));
const PublicOwnerReportPage = lazy(() => import('./pages/PublicOwnerReportPage'));
const PublicDepositEvidencePage = lazy(() => import('./pages/PublicDepositEvidencePage'));
const PublicUnitReadyPage = lazy(() => import('./pages/PublicUnitReadyPage'));
const VendorActionPage = lazy(() => import('./pages/VendorActionPage'));
const VendorLoginPage = lazy(() => import('./pages/vendor/VendorLoginPage'));
const VendorMagicEnterPage = lazy(() => import('./pages/vendor/VendorMagicEnterPage'));
const StaffMagicEnterPage = lazy(() => import('./pages/StaffMagicEnterPage'));
const VendorPortalLayout = lazy(() => import('./pages/vendor/VendorPortalLayout'));
const VendorJobsListPage = lazy(() => import('./pages/vendor/VendorJobsListPage'));
const VendorJobDetailPage = lazy(() => import('./pages/vendor/VendorJobDetailPage'));
const VendorEstimatesListPage = lazy(() => import('./pages/vendor/VendorEstimatesListPage'));
const VendorInspectionsListPage = lazy(() => import('./pages/vendor/VendorInspectionsListPage'));
const VendorInspectionDetailPage = lazy(() => import('./pages/vendor/VendorInspectionDetailPage'));
const VendorDocumentsPage = lazy(() => import('./pages/vendor/VendorDocumentsPage'));
const VendorTeamPage = lazy(() => import('./pages/vendor/VendorTeamPage'));
const VendorProfilePage = lazy(() => import('./pages/vendor/VendorProfilePage'));
const RateServicePage = lazy(() => import('./pages/RateServicePage'));
const SpeedReviewPage = lazy(() => import('./pages/SpeedReviewPage'));
const OrgAuditLogPage = lazy(() => import('./pages/OrgAuditLogPage'));
const OrgSettingsPage = lazy(() => import('./pages/OrgSettingsPage'));
const OnCallPage = lazy(() => import('./pages/OnCallPage'));
const OnCallCreateFlow = lazy(() => import('./pages/OnCallCreateFlow'));
const OnCallSchedulePage = lazy(() => import('./pages/OnCallSchedulePage'));
const OnCallSettingsPage = lazy(() => import('./pages/OnCallSettingsPage'));
const PropertyOnboardingWizardPage = lazy(() => import('./pages/PropertyOnboardingWizardPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));

import { detectPortal, portalLoginPath, applyPortalDocumentTitle } from './lib/portal';
import { safeNext } from './lib/safeNext';
import type { ReactNode } from 'react';
import { TypeScaleProvider, useTypeScale } from './theme/TypeScaleProvider';
import { antdTypeComponents, antdTypeTokens } from './theme/typeScale';

// Landing-page preload. A lazy page only starts downloading when its <Route>
// renders — and that is behind <ProtectedRoute>, i.e. AFTER /auth/me returns.
// Without this the page chunk would queue up behind auth instead of beside it
// (auth → chunk → render → page queries, one step longer than before the split).
// Starting the fetch here, at module evaluation, runs it in parallel with
// /auth/me. Only the pages people actually land on; everything else loads on
// navigation. A failed preload is swallowed — React.lazy will retry on render.
if (typeof window !== 'undefined') {
  const path = window.location.pathname;
  const landing: Array<[RegExp, () => Promise<unknown>]> = [
    [/^\/dashboard(\/|$)/, loadDashboardPage],
    [/^\/(my-queue)?\/?$/, loadMyQueuePage],
    [/^\/work-orders\/?$/, loadWorkOrdersPage],
    [/^\/make-ready\/?$/, loadMakeReadyListPage],
  ];
  const hit = landing.find(([re]) => re.test(path));
  if (hit) hit[1]().catch(() => undefined);
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function Loading() {
  return <div className="min-h-screen flex items-center justify-center"><Spin size="large" /></div>;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  // Portal-aware: on vendor.* the default login is the vendor portal's, on
  // ops.* the Command Center's. A signed-in vendor landing on /vendor/login
  // is bounced to /vendor/jobs by that page, so vendor.swiftlane.com/ always
  // resolves somewhere sensible.
  if (!user) {
    // Preserve the attempted destination so the login page can return the user
    // there instead of dumping them on the dashboard (spec 102 §4.6, Wave 0).
    const base = portalLoginPath();
    const dest = safeNext(location.pathname + location.search);
    const to = dest && dest !== '/' ? `${base}?next=${encodeURIComponent(dest)}` : base;
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading, isOpsUser } = useAuth();
  if (loading) return <Loading />;
  if (user) return <Navigate to={isOpsUser ? '/admin/orgs' : '/'} replace />;
  return <>{children}</>;
}

/** Only ops users can access these routes; org users get redirected to dashboard */
function OpsOnlyRoute({ children }: { children: ReactNode }) {
  const { isOpsUser } = useAuth();
  if (!isOpsUser) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Root redirect (PO fix round item 9) — `/` IS My Queue now; Dashboard lives
 * at `/dashboard`. Old deep links keep working:
 *   /?tab=board                        → /my-queue
 *   /?tab=reports (± section/…)       → /dashboard + preserved params
 *   /?section=mr-analytics            → /dashboard + preserved params
 *   / (bare)                          → /my-queue
 */
function RootRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.get('tab') === 'board') return <Navigate to="/my-queue" replace />;
  if (params.get('tab') === 'reports' || params.get('section')) {
    params.delete('tab'); // /dashboard renders reports directly — no tab param
    const qs = params.toString();
    return <Navigate to={`/dashboard${qs ? `?${qs}` : ''}`} replace />;
  }
  return <Navigate to="/my-queue" replace />;
}

/**
 * Legacy Analytics redirect (spec 179 D2, 2026-09-15) — the Analytics page is
 * folded into the Dashboard; its tables are sub-pages under `/dashboard/…`.
 * `/analytics-v2?propertyId=x&tab=vendors` → `/dashboard?propertyId=x` — the
 * building scope survives, `tab` / `metric` do not.
 */
function AnalyticsV2Redirect() {
  const location = useLocation();
  return <Navigate to={analyticsV2Redirect(location.search)} replace />;
}

/**
 * Legacy Alerts redirect (spec 110 §11) — the Alerts page is folded into
 * My Queue → Org-wide. Old links (`/alerts`, `/exceptions`) land on
 * `/my-queue?scope=org` with their filters mapped 1:1 (the org tab keys are
 * identical to the old `/alerts` tab keys; `view=snoozed` → `snoozed=1`).
 */
function LegacyAlertsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = new URLSearchParams();
  next.set('scope', 'org');
  const tab = params.get('tab');
  if (tab && ['wo', 'inspection', 'makeReady', 'residentMoves', 'integration'].includes(tab)) {
    next.set('tab', tab);
  }
  const priority = params.get('priority');
  if (priority) next.set('priority', priority.toUpperCase());
  const propertyId = params.get('propertyId');
  if (propertyId) next.set('propertyId', propertyId);
  const search = params.get('search');
  if (search) next.set('search', search);
  if (params.get('view') === 'snoozed' || params.get('snoozed') === 'true') next.set('snoozed', '1');
  return <Navigate to={`/my-queue?${next.toString()}`} replace />;
}

/** Org users always pass. Ops users need an active access token for org-scoped pages */
function OrgOrOpsRoute({ children }: { children: ReactNode }) {
  const { isOrgUser, isOpsUser, opsOrgAccess } = useAuth();
  if (isOrgUser) return <>{children}</>;
  if (isOpsUser && opsOrgAccess) return <>{children}</>;
  if (isOpsUser) return <Navigate to="/admin/orgs" replace />;
  return <>{children}</>;
}

/**
 * Capability-gated route (spec 113 W2 §4.2 — closes F-19). Like OrgOrOpsRoute,
 * but a page the role can't use redirects home instead of rendering a backend-
 * 403'd shell. `can()` treats ops-with-access as all-capable (mirrors the
 * backend ops-bypass) and fails OPEN for ORG_ADMIN on a capabilities-less
 * session; every other role fails closed. The nav already hides these items —
 * this only stops a direct-URL / bookmark from dead-ending. The backend still
 * enforces every action regardless of what the UI routes.
 */
function OrgCapRoute({ capability, children }: { capability: string; children: ReactNode }) {
  const { isOpsUser, opsOrgAccess, can } = useAuth();
  if (isOpsUser && !opsOrgAccess) return <Navigate to="/admin/orgs" replace />;
  return can(capability) ? <>{children}</> : <Navigate to="/" replace />;
}

/**
 * The All-bills register is the accounting seat's own workspace (PO,
 * 2026-08-12: *"visible only to accounting department, hide it for others"*).
 * Not a capability gate: `accounting.bills.view` still admits Admin/PM for
 * the two task pages and the disposition page's bills card — only this PAGE
 * is seat-gated. Ops-with-access passes like everywhere else; everyone else
 * routes home, same as OrgCapRoute.
 */
function AccountingSeatRoute({ children }: { children: ReactNode }) {
  const { isOpsUser, opsOrgAccess, user } = useAuth();
  if (isOpsUser && !opsOrgAccess) return <Navigate to="/admin/orgs" replace />;
  return canSeeAccountingBillsPage(user?.role, isOpsUser && !!opsOrgAccess)
    ? <>{children}</>
    : <Navigate to="/" replace />;
}

/**
 * `/login` is the PM login by default, but on a persona subdomain it remaps
 * to that portal's login so "just go to the domain" works:
 *   vendor.swiftlane.com/login → /vendor/login
 *   ops.swiftlane.com/login    → /ops/login
 * The portal-specific login routes stay reachable on EVERY host (magic links
 * and bookmarks must never break) — only the default is host-dependent.
 */
function DefaultLoginRoute() {
  const portal = detectPortal();
  if (portal !== 'pm') return <Navigate to={portalLoginPath(portal)} replace />;
  return <PublicRoute><LoginPage /></PublicRoute>;
}

// Tab title reflects the portal (vendor / command center). Module scope —
// the hostname can't change without a full page load, so once is enough.
applyPortalDocumentTitle();

export default function App() {
  // LocaleProvider owns the active locale + the AntD locale object it feeds to
  // ConfigProvider below. With clean localStorage the locale is 'en' and
  // antdLocale is undefined → AntD keeps today's exact default (spec 91 D2).
  return (
    <LocaleProvider>
      <TypeScaleProvider>
        <AppShell />
      </TypeScaleProvider>
    </LocaleProvider>
  );
}

function AppShell() {
  const { antdLocale } = useLocale();
  // The type scale (avatar menu → Text size). Ant Design's sizes are numbers,
  // not CSS, so its tokens are rebuilt from the scale here; everything else in
  // the page reads the `--t-*` variables that the same switch drives.
  const { typeScale } = useTypeScale();
  // colorTextSecondary darkened from AntD's light default to a dark grey so
  // "secondary" text reads clearly, not washed out (customer feedback).
  // Input/dropdown placeholders (colorTextPlaceholder) are also darkened below —
  // AntD's default rgba(0,0,0,.25) ≈ #bfbfbf read as too light/greyed.
  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          ...antdTypeTokens(typeScale),
          colorPrimary: BRAND,
          colorInfo: BRAND,
          colorLink: BRAND,
          // AntD does NOT use the brand as-is: it GENERATES ~10 derived steps
          // from it, and its generator rotates light tints toward violet
          // (#4119F3 → bg #eee6ff, hue 259). That silently re-introduced purple
          // on every derived surface — info Alerts, selected rows, Select/Menu
          // hover, DatePicker cells — none of which exist as a hex in our source,
          // so no sweep could find them. Pin every step to our own tokens instead.
          colorPrimaryBg: BRAND_50,
          colorPrimaryBgHover: BRAND_100,
          colorPrimaryBorder: BRAND_BORDER,
          colorPrimaryBorderHover: BRAND_300,
          colorPrimaryHover: BRAND_600,
          colorPrimaryActive: BRAND_700,
          colorPrimaryText: BRAND,
          colorPrimaryTextHover: BRAND_600,
          colorPrimaryTextActive: BRAND_700,
          colorInfoBg: BRAND_50,
          colorInfoBgHover: BRAND_100,
          colorInfoBorder: BRAND_BORDER,
          colorInfoBorderHover: BRAND_300,
          colorInfoHover: BRAND_600,
          colorInfoActive: BRAND_700,
          colorInfoText: BRAND,
          colorInfoTextHover: BRAND_600,
          colorInfoTextActive: BRAND_700,
          colorLinkHover: BRAND_600,
          colorLinkActive: BRAND_700,
          // Muted brick-red — urgent/destructive actions without the default
          // AntD neon (#ff4d4f). Aligns with design-system danger intent.
          colorError: '#c85852',
          colorErrorHover: '#b54a44',
          colorErrorActive: '#a64a44',
          colorErrorBg: '#FFE8ED',
          colorErrorBorder: '#efb8b4',
          borderRadius: 8,
          borderRadiusLG: 8,
          borderRadiusSM: 8,
          colorTextSecondary: '#363d48',
          // Form helper/extra text, list descriptions, etc. AntD's default
          // (rgba(0,0,0,.45) ≈ #8c8c8c) reads as unreadable light grey — lift
          // it to the readable dark-grey used elsewhere (no-light-grey rule).
          colorTextDescription: '#4d5562',
          // Input / select / date-picker placeholders — darker than AntD's washed
          // default (rgba(0,0,0,.25) ≈ #bfbfbf) so filter placeholders read clearly.
          // Still lighter than real values / secondary text, so it stays a hint.
          colorTextPlaceholder: '#8b909c',
          // Field / card borders — AntD's default (~#d9d9d9) washes out on the
          // lavender canvas. Use design-system `line` (#D4D5D9) so text boxes
          // and selects read as real fields (same complaint class as light-grey text).
          colorBorder: '#D4D5D9',
          colorBorderSecondary: '#E3DFEE',
          fontFamily:
            "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        },
        components: {
          ...antdTypeComponents(typeScale),
          Button: { primaryShadow: 'none' },
          Input: { activeBorderColor: BRAND, hoverBorderColor: BRAND_400 },
          Select: { activeBorderColor: BRAND, hoverBorderColor: BRAND_400 },
          DatePicker: { activeBorderColor: BRAND, hoverBorderColor: BRAND_400 },
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<Loading />}>
            <Routes>
              {/* Public auth routes */}
              <Route path="/login" element={<DefaultLoginRoute />} />
              {/* Public password reset (staff). No auth, no AppLayout — the
                  emailed 6-digit code is the proof of ownership. Wrapped in
                  PublicRoute like the login pages: a signed-in user has no
                  business here and change-password lives in the app. */}
              <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
              <Route path="/ops/login" element={<PublicRoute><OpsLoginPage /></PublicRoute>} />

              {/* Protected app shell */}
              <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                {/* Org user routes (also accessible by ops with active access token) */}
                {/* `/` = My Queue (PO item 9); Dashboard (reports) at /dashboard. */}
                <Route path="/" element={<OrgOrOpsRoute><RootRedirect /></OrgOrOpsRoute>} />
                <Route path="/dashboard" element={<OrgOrOpsRoute><DashboardPage /></OrgOrOpsRoute>} />
                {/* The Dashboard's sub-pages (spec 179 §5.5) — no menu item; each is a door
                    opened from a Dashboard section, carrying its scope + period (I-1). */}
                <Route path="/dashboard/by/:table" element={<OrgOrOpsRoute><ComparisonPage /></OrgOrOpsRoute>} />
                <Route path="/dashboard/ai-value" element={<OrgOrOpsRoute><AiValuePage /></OrgOrOpsRoute>} />
                <Route path="/dashboard/items-changed" element={<OrgOrOpsRoute><ItemsChangedPage /></OrgOrOpsRoute>} />
                <Route path="/dashboard/metrics" element={<OrgOrOpsRoute><AllMetricsPage /></OrgOrOpsRoute>} />
                <Route path="/dashboard/make-ready" element={<OrgOrOpsRoute><MakeReadyAnalyticsPage /></OrgOrOpsRoute>} />
                <Route path="/work-orders" element={<OrgOrOpsRoute><WorkOrdersPage /></OrgOrOpsRoute>} />
                <Route path="/work-orders/new" element={<OrgCapRoute capability="work_orders.create"><CreateWorkOrderPage /></OrgCapRoute>} />
                <Route path="/work-orders/:id" element={<OrgOrOpsRoute><WorkOrderDetailPage /></OrgOrOpsRoute>} />
                <Route path="/work-orders/:id/edit" element={<OrgCapRoute capability="work_orders.operate"><EditWorkOrderPage /></OrgCapRoute>} />
                <Route path="/vendors" element={<OrgOrOpsRoute><VendorsPage /></OrgOrOpsRoute>} />
                <Route path="/vendors/new" element={<OrgCapRoute capability="vendors.manage"><VendorCreatePage /></OrgCapRoute>} />
                {/* Static paths before :id so they never collide with a vendor UUID. */}
                <Route path="/vendors/matrix" element={<Navigate to="/vendor-preferences" replace />} />
                <Route path="/vendors/:id/edit" element={<OrgCapRoute capability="vendors.manage"><VendorEditPage /></OrgCapRoute>} />
                <Route path="/vendors/:id" element={<OrgOrOpsRoute><VendorDetailPage /></OrgOrOpsRoute>} />
                <Route path="/properties" element={<OrgOrOpsRoute><PropertiesPage /></OrgOrOpsRoute>} />
                <Route path="/properties/new" element={<OrgCapRoute capability="properties.manage"><PropertyCreatePage /></OrgCapRoute>} />
                <Route path="/properties/:id/edit" element={<OrgCapRoute capability="properties.manage"><PropertyEditPage /></OrgCapRoute>} />
                <Route path="/properties/:id" element={<OrgOrOpsRoute><PropertyDetailPage /></OrgOrOpsRoute>} />
                <Route path="/properties/:propertyId/units/:unitId" element={<OrgOrOpsRoute><UnitDetailPage /></OrgOrOpsRoute>} />
                <Route path="/onboarding/manual/csv" element={<OrgCapRoute capability="properties.manage"><ManualPropertiesCsvUploadPage /></OrgCapRoute>} />
                <Route path="/team/invite-csv" element={<OrgCapRoute capability="team.manage"><TeamInviteCsvPage /></OrgCapRoute>} />
                <Route path="/team" element={<OrgCapRoute capability="team.manage"><TeamPage /></OrgCapRoute>} />
                <Route path="/account" element={<OrgOrOpsRoute><AccountSettingsPage /></OrgOrOpsRoute>} />
                <Route path="/dispatch-rules" element={<OrgCapRoute capability="dispatch.manage"><DispatchRulesPage /></OrgCapRoute>} />
                <Route path="/knowledge-base" element={<OrgOrOpsRoute><KnowledgeBasePage /></OrgOrOpsRoute>} />
                <Route path="/analytics" element={<OrgOrOpsRoute><AnalyticsPage /></OrgOrOpsRoute>} />
                <Route path="/vendor-preferences" element={<OrgCapRoute capability="dispatch.manage"><VendorPreferencesPage /></OrgCapRoute>} />
                <Route path="/rules/repair-replace" element={<OrgCapRoute capability="dispatch.manage"><RepairReplaceRulesPage /></OrgCapRoute>} />
                <Route path="/nte-config" element={<OrgCapRoute capability="dispatch.manage"><NTEConfigPage /></OrgCapRoute>} />
                {/* Alerts page retired (spec 110 D3) — folded into My Queue → Org-wide. */}
                <Route path="/alerts" element={<LegacyAlertsRedirect />} />
                <Route path="/exceptions" element={<LegacyAlertsRedirect />} />
                <Route path="/assets" element={<OrgCapRoute capability="assets.manage"><AssetRegistryPage /></OrgCapRoute>} />
                <Route path="/assets/new" element={<OrgCapRoute capability="assets.manage"><AssetCreatePage /></OrgCapRoute>} />
                <Route path="/assets/:id/edit" element={<OrgCapRoute capability="assets.manage"><AssetEditPage /></OrgCapRoute>} />
                <Route path="/assets/:id" element={<OrgCapRoute capability="assets.manage"><AssetDetailPage /></OrgCapRoute>} />
                <Route path="/templates" element={<OrgCapRoute capability="wo_templates.manage"><WorkOrderTemplatesPage /></OrgCapRoute>} />
                <Route path="/owners" element={<OrgOrOpsRoute><OwnersPage /></OrgOrOpsRoute>} />
                <Route path="/owners/:ownerId/dashboard" element={<OrgOrOpsRoute><OwnerDashboardPage /></OrgOrOpsRoute>} />
                {/* The LIST is inspections.view (ALL_PORTAL) — MAINTENANCE conducts the walks
                    and must be able to reach the list. The page gates every ADMIN_PM write
                    action behind inspections.configure; the backend GETs were already open. */}
                <Route path="/inspections" element={<OrgCapRoute capability="inspections.view"><InspectionsPage /></OrgCapRoute>} />
                <Route path="/inspections/templates" element={<OrgCapRoute capability="inspections.configure"><InspectionTemplatesPage /></OrgCapRoute>} />
                <Route path="/inspections/templates/new" element={<OrgCapRoute capability="inspections.configure"><InspectionTemplateDetailPage /></OrgCapRoute>} />
                <Route path="/inspections/templates/:id/history" element={<OrgCapRoute capability="inspections.configure"><InspectionTemplateHistoryPage /></OrgCapRoute>} />
                <Route path="/inspections/templates/:id" element={<OrgCapRoute capability="inspections.configure"><InspectionTemplateDetailPage /></OrgCapRoute>} />
                <Route path="/inspections/categories" element={<OrgCapRoute capability="inspections.configure"><InspectionCategoriesPage /></OrgCapRoute>} />
                <Route path="/inspections/:id/walk" element={<OrgOrOpsRoute><InspectionWalkPage /></OrgOrOpsRoute>} />
                <Route path="/inspections/:id/campaign" element={<OrgOrOpsRoute><InspectionCampaignPage /></OrgOrOpsRoute>} />
                {/* Spec 135 §12.1 — the create-work-order wizard (was a modal in 3 hosts).
                    Distinct from `/work-orders/new`, the manual create form. */}
                <Route path="/inspections/:inspectionId/create-work-order" element={<OrgCapRoute capability="work_orders.create"><CreateWorkOrderFromFindingsPage /></OrgCapRoute>} />
                <Route path="/action-items" element={<OrgCapRoute capability="inspections.action_items"><ActionItemsPage /></OrgCapRoute>} />
                <Route path="/compliance" element={<OrgOrOpsRoute><ComplianceRegistryPage /></OrgOrOpsRoute>} />
                <Route path="/compliance/obligations/:id" element={<OrgOrOpsRoute><ComplianceObligationDetailPage /></OrgOrOpsRoute>} />
                <Route path="/notices" element={<OrgOrOpsRoute><NoticesPage /></OrgOrOpsRoute>} />
                <Route path="/dispositions" element={<OrgOrOpsRoute><DispositionsPage /></OrgOrOpsRoute>} />
                {/* Must precede `/dispositions/:id` — otherwise the param route
                    swallows "price-list" and renders a not-found disposition. */}
                <Route path="/dispositions/price-list" element={<OrgOrOpsRoute><DispositionPriceListPage /></OrgOrOpsRoute>} />
                <Route path="/dispositions/:id" element={<OrgOrOpsRoute><DispositionDetailPage /></OrgOrOpsRoute>} />
                <Route path="/recurring-inspections" element={<OrgCapRoute capability="inspections.configure"><RecurringInspectionsPage /></OrgCapRoute>} />
                <Route path="/recurring-inspections/:id" element={<OrgCapRoute capability="inspections.configure"><RecurringInspectionDetailPage /></OrgCapRoute>} />
                <Route path="/vendor-batches" element={<OrgCapRoute capability="vendors.manage"><VendorBatchesPage /></OrgCapRoute>} />
                <Route path="/estimates" element={<OrgCapRoute capability="estimates.view"><EstimatesPage /></OrgCapRoute>} />
                <Route path="/my-queue" element={<OrgOrOpsRoute><MyQueuePage /></OrgOrOpsRoute>} />
                <Route path="/inbox" element={<OrgOrOpsRoute><InboxPage /></OrgOrOpsRoute>} />
                <Route path="/make-ready" element={<OrgOrOpsRoute><MakeReadyListPage /></OrgOrOpsRoute>} />
                <Route path="/make-ready/settings" element={<OrgCapRoute capability="make_ready.configure"><MakeReadySettingsPage /></OrgCapRoute>} />
                <Route path="/make-ready/:id" element={<OrgOrOpsRoute><MakeReadyFolderPage /></OrgOrOpsRoute>} />
                <Route path="/vendor-portal-preview" element={<OrgCapRoute capability="vendors.manage"><VendorPortalPreviewPage /></OrgCapRoute>} />
                <Route path="/calendar" element={<OrgOrOpsRoute><CalendarPage /></OrgOrOpsRoute>} />
                <Route path="/analytics-v2" element={<OrgOrOpsRoute><AnalyticsV2Redirect /></OrgOrOpsRoute>} />
                <Route path="/automation" element={<OrgCapRoute capability="sla.manage"><ScheduledActionsPage /></OrgCapRoute>} />
                <Route path="/ai-knowledge" element={<OrgCapRoute capability="ai_memory.org"><AIMemoryPage /></OrgCapRoute>} />
                <Route path="/ai-memory" element={<Navigate to="/ai-knowledge" replace />} />
                <Route path="/coordinator-coach" element={<OrgCapRoute capability="analytics.org"><CoordinatorCoachPage /></OrgCapRoute>} />
                <Route path="/speed-review" element={<OrgCapRoute capability="work_orders.operate"><SpeedReviewPage /></OrgCapRoute>} />
                <Route path="/integrations" element={<OrgCapRoute capability="integrations.manage"><IntegrationsPage /></OrgCapRoute>} />
                <Route path="/integrations/connected-accounts" element={<OrgCapRoute capability="integrations.manage"><ConnectedAccountsPage /></OrgCapRoute>} />
                <Route path="/integrations/appfolio" element={<OrgCapRoute capability="integrations.manage"><AppFolioOnboardingPage /></OrgCapRoute>} />
                <Route path="/integrations/yardi" element={<OrgCapRoute capability="integrations.manage"><YardiOnboardingPage /></OrgCapRoute>} />
                <Route path="/integrations/buildium" element={<OrgCapRoute capability="integrations.manage"><BuildiumOnboardingPage /></OrgCapRoute>} />
                <Route path="/integrations/buildium/install" element={<OrgCapRoute capability="integrations.manage"><BuildiumOnboardingPage /></OrgCapRoute>} />
                <Route path="/integrations/appfolio/property-onboarding/:swiftopsPropertyId" element={<OrgCapRoute capability="integrations.manage"><PropertyOnboardingWizardPage /></OrgCapRoute>} />
                <Route path="/sla" element={<OrgCapRoute capability="sla.manage"><SLAConfigPage /></OrgCapRoute>} />
                <Route path="/sla-config" element={<Navigate to="/sla" replace />} />
                <Route path="/invoices" element={<OrgOrOpsRoute><InvoicesPage /></OrgOrOpsRoute>} />
                <Route path="/accounting" element={<AccountingSeatRoute><AccountingBillsPage /></AccountingSeatRoute>} />
                {/* The two accounting TASK pages (spec 145). Self-contained on
                    purpose: the work, its bills and its documents come to the
                    reader, so a charge is recorded without opening a work order,
                    a make-ready folder or an inspection. */}
                <Route path="/accounting/turns/:makeReadyId" element={<OrgCapRoute capability="accounting.bills.view"><AccountingTurnTaskPage /></OrgCapRoute>} />
                <Route path="/accounting/work-orders/:workOrderId" element={<OrgCapRoute capability="accounting.bills.view"><AccountingWorkOrderTaskPage /></OrgCapRoute>} />
                {/* The leasing TASK page — one apartment coming back to market.
                    Gated on the turn's own VIEW capability (ALL_PORTAL) rather
                    than a leasing one: there is no LEASING role in the enum, and
                    the seat is a DEPARTMENT rule that lives in AppLayout /
                    lib/leasingSurface.ts. The backend still scopes the read. */}
                <Route path="/leasing/turns/:makeReadyId" element={<OrgCapRoute capability="make_ready.view"><LeasingTurnTaskPage /></OrgCapRoute>} />
                <Route path="/surveys" element={<OrgCapRoute capability="analytics.org"><SurveysPage /></OrgCapRoute>} />
                <Route path="/audit-log" element={<OrgCapRoute capability="audit.view"><OrgAuditLogPage /></OrgCapRoute>} />
                <Route path="/on-call" element={<OrgCapRoute capability="oncall.view"><OnCallPage /></OrgCapRoute>} />
                {/* Static path before :id so "new" is never treated as a schedule id. */}
                <Route path="/on-call/new" element={<OrgCapRoute capability="oncall.manage"><OnCallCreateFlow /></OrgCapRoute>} />
                <Route path="/on-call/:id" element={<OrgCapRoute capability="oncall.view"><OnCallSchedulePage /></OrgCapRoute>} />
                <Route path="/on-call/:id/settings" element={<OrgCapRoute capability="oncall.view"><OnCallSettingsPage /></OrgCapRoute>} />
                <Route path="/org-settings" element={<OrgCapRoute capability="settings.view"><OrgSettingsPage /></OrgCapRoute>} />

                {/* Command Center — ops only */}
                <Route path="/admin/orgs" element={<OpsOnlyRoute><OrgListPage /></OpsOnlyRoute>} />
                <Route path="/admin/orgs/new" element={<OpsOnlyRoute><CreateOrgPage /></OpsOnlyRoute>} />
                <Route path="/admin/orgs/:id" element={<OpsOnlyRoute><OrgDetailPage /></OpsOnlyRoute>} />
                <Route path="/admin/access-tokens" element={<OpsOnlyRoute><AccessTokensPage /></OpsOnlyRoute>} />
                <Route path="/admin/ops-team" element={<OpsOnlyRoute><OpsTeamPage /></OpsOnlyRoute>} />
                <Route path="/admin/demo-accounts/new" element={<OpsOnlyRoute><DemoAccountNewPage /></OpsOnlyRoute>} />
              </Route>

              {/* Resident test page — standalone, no auth guard */}
              <Route path="/resident-test" element={<ResidentTestPage />} />
              <Route path="/resident-upload" element={<ResidentUploadPage />} />
              <Route path="/vendor-job/:token" element={<VendorJobPage />} />
              <Route path="/vendor-action/:token" element={<VendorActionPage />} />

              {/* Vendor login portal — public; the portal shell guards its own
                  session (NOT the staff ProtectedRoute). Token IS the auth for
                  the magic-enter route. */}
              <Route path="/vendor/login" element={<VendorLoginPage />} />
              <Route path="/vendor/enter/:token" element={<VendorMagicEnterPage />} />
              {/* Staff email auto-login (spec 102). Bare route — handles both
                  logged-out and logged-in visitors (identity precedence). */}
              <Route path="/enter/:token" element={<StaffMagicEnterPage />} />
              <Route path="/vendor" element={<VendorPortalLayout />}>
                <Route index element={<Navigate to="/vendor/jobs" replace />} />
                <Route path="jobs" element={<VendorJobsListPage />} />
                <Route path="jobs/:id" element={<VendorJobDetailPage />} />
                <Route path="estimates" element={<VendorEstimatesListPage />} />
                <Route path="inspections" element={<VendorInspectionsListPage />} />
                <Route path="inspections/:id" element={<VendorInspectionDetailPage />} />
                <Route path="documents" element={<VendorDocumentsPage />} />
                <Route path="team" element={<VendorTeamPage />} />
                <Route path="profile" element={<VendorProfilePage />} />
                {/* Typo'd /vendor/* paths land on the job list, not the PM
                    login (the global * catch-all bounces through / which is
                    staff-guarded — confusing for a signed-in vendor). */}
                <Route path="*" element={<Navigate to="/vendor/jobs" replace />} />
              </Route>
              <Route path="/cascade/:token" element={<CascadeActionPage />} />
              {/* Public turn-summary artifact (spec 99 §18 W3) — no login; the
                  token IS the auth. EN-only leasing handoff view. */}
              <Route path="/turn-summary/:token" element={<PublicTurnSummaryPage />} />
              {/* The owner's no-login monthly report (spec 179 Phase 2 §5.4) —
                  no login; the ?token= query param IS the auth. */}
              <Route path="/owner-report/:id" element={<PublicOwnerReportPage />} />
              {/* The resident's deposit-evidence page (spec 161 §5) — no login;
                  the token IS the auth. Civ. Code § 1950.5(h)(2)(D) names a link
                  as a way to PROVIDE the photographs, and it is the only channel
                  that carries all three sets: the PDF drops them at 3 per charge
                  and 24 per statement, and the email strips them all at ~5 MB. */}
              <Route path="/deposit-evidence/:token" element={<PublicDepositEvidencePage />} />
              {/* Public unit-ready listing — no login; the token IS the auth.
                  EN-only, for an EXTERNAL leasing/marketing contact: listing
                  facts only, never turn data (PO ruling 2026-08-12). */}
              <Route path="/unit-ready/:token" element={<PublicUnitReadyPage />} />
              <Route path="/rate" element={<RateServicePage />} />
              {/* Public invite acceptance — token IS the auth, same security
                  model as vendor/cascade magic links. NOT wrapped in
                  PublicRoute because an already-signed-in user may still
                  need to accept an invite to a different org. */}
              <Route path="/accept-invite" element={<AcceptInvitePage />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ConfigProvider>
  );
}
